import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Ban,
  Calendar,
  Check,
  ChevronDown,
  Eye,
  Mail,
  MoreHorizontal,
  Phone,
  PhoneCall,
  Search,
  X,
} from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { TabsContent } from "@/shared/components/ui/tabs";

import type { CandidateListItem } from "@/modules/hrPortal/types";
import { CANDIDATE_STATUS_OPTIONS } from "@/modules/hrPortal/constants/candidateStatus";
import { getScoreColorClass } from "@/modules/hrPortal/utils/scoreHelpers";
import { formatDate, getInitials } from "@/modules/hrPortal/utils/formatters";
import { DEPARTMENTS } from "@/shared/constants/department";

interface AllocationOwner {
  id?: string | null;
  name?: string | null;
  fullName?: string | null;
  role?: string | null;
}

interface CandidateAllocationInfo {
  status?: string | null;
  allocationStatus?: string | null;
  allocationTeamId?: string | null;
  allocationApplicationId?: string | null;
  allocationClaimedById?: string | null;
  allocationStartedAt?: string | null;
  isAllocated?: boolean;
  isAllocatedToCurrentTeam?: boolean;
  isAllocatedToAnotherTeam?: boolean;
  isBlacklisted?: boolean;
  blacklistedAt?: string | null;
  blacklistExpiresAt?: string | null;
  ownerName?: string | null;
  ownerRole?: string | null;
  allocationOwnerName?: string | null;
  allocationOwnerRole?: string | null;
  allocationClaimedBy?: AllocationOwner | null;
  owner?: AllocationOwner | null;
}

interface CandidateApplicationSummary {
  id?: string;
  applicationId: string;
  jobId?: string | null;
  jobTitle?: string | null;
  department?: string | null;
  atsScore?: number | string | null;
  status?: Array<{ status?: string;[key: string]: unknown }>;
  currentStatus?: string | null;
  createdAt?: string | null;
  referredByName?: string | null;
}

type CandidateWithAllocation = CandidateListItem & {
  applications?: CandidateApplicationSummary[];
  allocation?: CandidateAllocationInfo | null;
  allocationStatus?: string | null;
  allocationOwnerName?: string | null;
  allocationOwnerRole?: string | null;
  allocationClaimedBy?: AllocationOwner | null;
  owner?: AllocationOwner | null;
  isBlacklisted?: boolean;
  blacklistExpiresAt?: string | null;
  allocationStartedAt?: string | null;
};

interface CandidatesTabProps {
  candidates: CandidateListItem[];
  pagination: any;
  loading: boolean;
  error: string | null;
  isHRAdmin: boolean;
  onScheduleVoiceCalls: () => void;
  onPageChange: (page: number) => void;

  onFiltersChange: (filters: {
    search: string;
    department: string;
    status: string;
    minATSScore: string;
  }) => void;
}

type AllocationFilterValue = "Available" | "Allocated" | "Blacklisted";

const normalizeDepartment = (value: unknown): string => {
  const department = String(value ?? "").trim();

  if (!department) return "";

  const normalized = department.toLowerCase();

  if (
    normalized === "hr" ||
    normalized === "human resource" ||
    normalized === "human resources"
  ) {
    return "Human Resource";
  }

  return department;
};

const getLatestStatusValue = (history: unknown, fallback?: unknown): string => {
  if (Array.isArray(history) && history.length > 0) {
    // Status history is treated as an ordered sequence of updates.
    // Use the last valid status entry, not timestamp sorting.
    for (let index = history.length - 1; index >= 0; index -= 1) {
      const entry = history[index];
      if (!entry || typeof entry !== "object") continue;

      const status = (entry as Record<string, unknown>).status;
      if (status !== null && status !== undefined && String(status).trim()) {
        return String(status);
      }
    }
  }

  if (fallback !== null && fallback !== undefined && String(fallback).trim()) {
    return String(fallback);
  }

  return "N/A";
};

const normalizeCandidateFilterStatus = (
  candidate: CandidateListItem
): string => {
  const rawStatus = String(
    candidate.currentStage?.status ??
    candidate.currentStatus ??
    (Array.isArray(candidate.status)
      ? candidate.status[candidate.status.length - 1]?.status
      : candidate.status) ??
    ""
  )
    .trim()
    .toUpperCase()
    .replace(/-/g, "_")
    .replace(/\s+/g, "_");

  switch (rawStatus) {
    case "SHORTLISTED":
    case "CONSIDER":
      return "Shortlisted";

    case "UNDER_REVIEW":
      return "Under Review";

    case "SELECTED":
      return "Selected";

    case "REJECTED":
    case "NOT_SELECTED":
      return "Rejected";

    case "BLACKLISTED":
      return "Blacklisted";

    default:
      return rawStatus;
  }
};

const getStatusColor = (status: string): string => {
  switch (status?.toUpperCase().replace(/_/g, " ")) {
    case "SELECTED":
    case "HIRED":
      return "bg-green-100 text-green-700 border-green-200";

    case "REJECTED":
      return "bg-red-100 text-red-700 border-red-200";

    case "BLACKLISTED":
      return "bg-gray-900 text-white border-gray-900";

    case "PENDING ATS":
      return "bg-yellow-100 text-yellow-700 border-yellow-200";

    case "UNDER REVIEW":
      return "bg-blue-100 text-blue-700 border-blue-200";

    case "SHORTLISTED":
    case "SHORTLISTED FOR HR ROUND":
    case "SHORTLISTED FOR TECHNICAL ROUND":
    case "SHORTLISTED FOR MANAGEMENT ROUND":
      return "bg-yellow-100 text-yellow-700 border-yellow-200";

    case "HR INTERVIEW":
      return "bg-purple-100 text-purple-700 border-purple-200";

    case "TECHNICAL ROUND":
    case "BUSINESS ROUND":
      return "bg-indigo-100 text-indigo-700 border-indigo-200";

    case "MANAGEMENT ROUND":
      return "bg-violet-100 text-violet-700 border-violet-200";

    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

const getAllocationDetails = (
  candidate: CandidateListItem,
  applicationId?: string | null
) => {
  const item = candidate as CandidateWithAllocation;
  const allocation: CandidateAllocationInfo = item.allocation ?? {};

  const allocationStatus = String(
    allocation.status ??
    allocation.allocationStatus ??
    item.allocationStatus ??
    ""
  ).toUpperCase();

  const isBlacklisted =
    Boolean(allocation.isBlacklisted ?? item.isBlacklisted) ||
    allocationStatus === "BLACKLISTED";

  const isAllocated =
    Boolean(allocation.isAllocated) ||
    Boolean(allocation.allocationTeamId) ||
    allocationStatus === "ALLOCATED" ||
    allocationStatus === "ALLOTTED" ||
    allocationStatus === "CLAIMED";

  const allocationApplicationId =
    allocation.allocationApplicationId ?? null;

  const isAllocatedToCurrentApplication =
    Boolean(
      isAllocated &&
      allocationApplicationId &&
      applicationId &&
      String(allocationApplicationId) === String(applicationId)
    );

  const owner =
    allocation.allocationClaimedBy ??
    allocation.owner ??
    item.allocationClaimedBy ??
    item.owner ??
    null;

  const ownerName =
    allocation.ownerName ??
    allocation.allocationOwnerName ??
    item.allocationOwnerName ??
    owner?.name ??
    owner?.fullName ??
    null;

  const ownerRole =
    allocation.ownerRole ??
    allocation.allocationOwnerRole ??
    item.allocationOwnerRole ??
    owner?.role ??
    null;

  const isAllocatedToAnotherTeam =
    allocation.isAllocatedToAnotherTeam ??
    (isAllocated && !allocation.isAllocatedToCurrentTeam);

  return {
    isBlacklisted,
    isAllocated,
    isAllocatedToCurrentTeam: Boolean(
      allocation.isAllocatedToCurrentTeam
    ),
    isAllocatedToAnotherTeam: Boolean(isAllocatedToAnotherTeam),
    canManageProcess:
      !isBlacklisted &&
      !Boolean(isAllocatedToAnotherTeam) &&
      (
        !isAllocated ||
        isAllocatedToCurrentApplication
      ),
    ownerName,
    ownerRole,
    allocationStartedAt:
      allocation.allocationStartedAt ??
      item.allocationStartedAt ??
      null,
    blacklistExpiresAt:
      allocation.blacklistExpiresAt ??
      item.blacklistExpiresAt ??
      null,
  };
};

const getAllocationLabel = (
  details: ReturnType<typeof getAllocationDetails>
): string => {
  if (details.isBlacklisted) return "Blacklisted";

  if (!details.isAllocated) return "Available";

  if (details.ownerName) {
    return `Allocated to ${details.ownerName}${details.ownerRole ? ` (${details.ownerRole})` : ""
      }`;
  }

  return "Allocated";
};

const getAllocationBadgeClass = (
  details: ReturnType<typeof getAllocationDetails>
): string => {
  if (details.isBlacklisted) {
    return "border-red-200 bg-red-50 text-red-700";
  }

  if (details.isAllocated) {
    return "border-amber-200 bg-amber-50 text-amber-800";
  }

  return "border-green-200 bg-green-50 text-green-700";
};

// Parent aur child table ke columns align rakhne ke liye fixed widths.
// Child table card (mx-5 + 1px border) parent se 21px andar hai, isliye
// child ka pehla aur aakhri column 21px chhota hai. Baaki columns exactly parent ke neeche aate hain.
// Parent aur child table ke columns align rakhne ke liye fixed widths.
const COLUMN_WIDTHS = [
  380, // Candidate
  200, // Position
  150, // Department
  110, // ATS Score
  220, // Status
  260, // Allocation Status
  115, // Applications
  120, // Voice Call
  110, // Applied
  90,  // Actions
];

const CHILD_CARD_OFFSET = 21;

const sumWidths = (widths: number[]) =>
  widths.reduce((sum, width) => sum + width, 0);

// HR Admin ke liye additional Referred By column.
const getTableWidths = (isHRAdmin: boolean) => {
  const widths = isHRAdmin
    ? [
      ...COLUMN_WIDTHS.slice(0, 2),
      170, // Referred By
      ...COLUMN_WIDTHS.slice(2),
    ]
    : COLUMN_WIDTHS;

  const childWidths = widths.map((width, index) =>
    index === 0 || index === widths.length - 1
      ? width - CHILD_CARD_OFFSET
      : width
  );

  return {
    widths,
    childWidths,
    tableWidth: sumWidths(widths),
    childTableWidth: sumWidths(childWidths),
  };
};

function ColGroup({ widths = COLUMN_WIDTHS }: { widths?: number[] }) {
  return (
    <colgroup>
      {widths.map((width, index) => (
        <col key={index} style={{ width }} />
      ))}
    </colgroup>
  );
}

interface MultiSelectFilterProps {
  label: string;
  options: string[];
  selected: string[];
  onChange: (values: string[]) => void;
  singleSelect?: boolean;
}

function MultiSelectFilter({
  label,
  options,
  selected,
  onChange,
  singleSelect = false,
}: MultiSelectFilterProps) {
  const [open, setOpen] = useState(false);

  const toggleOption = (option: string) => {
    if (singleSelect) {
      onChange(selected.includes(option) ? [] : [option]);
      setOpen(false);
      return;
    }

    onChange(
      selected.includes(option)
        ? selected.filter((value) => value !== option)
        : [...selected, option]
    );
  };

  const selectAll = () => {
    onChange([]);
    setOpen(false);
  };

  const displayLabel =
    selected.length === 0 ? label : selected[0];

  return (
    <div className="relative min-w-0">
      <Button
        type="button"
        variant="outline"
        className="h-10 w-full justify-between gap-2 rounded-md border-slate-200 bg-white px-3 text-sm font-normal text-slate-700 shadow-none transition hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-blue-100"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="truncate">{displayLabel}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-slate-500 transition-transform ${open ? "rotate-180" : ""
            }`}
        />
      </Button>

      {open && (
        <>
          <button
            type="button"
            aria-label={`Close ${label} filter`}
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />

          <div
            role="listbox"
            aria-label={label}
            aria-multiselectable={singleSelect ? undefined : true}
            className="absolute left-0 top-full z-50 mt-1 max-h-72 w-full min-w-[220px] overflow-y-auto rounded-md border border-slate-200 bg-white p-1 shadow-[0_4px_8px_rgba(15,23,42,0.16)]"
          >
            <button
              type="button"
              role="option"
              aria-selected={selected.length === 0}
              className={`flex h-9 w-full items-center gap-2 rounded-sm px-2 text-left text-sm font-normal transition-colors ${selected.length === 0
                ? "bg-slate-100 text-slate-900"
                : "text-slate-700 hover:bg-slate-50"
                }`}
              onClick={selectAll}
            >
              <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                {selected.length === 0 && (
                  <Check
                    className="h-4 w-4 text-slate-900"
                    strokeWidth={2}
                  />
                )}
              </span>
              <span className="min-w-0 flex-1 truncate">All</span>
            </button>

            {options.map((option) => {
              const isSelected = selected.includes(option);

              return (
                <button
                  key={option}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`flex min-h-8 w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm font-normal transition-colors ${isSelected
                    ? "bg-slate-100 text-slate-900"
                    : "text-slate-700 hover:bg-slate-50"
                    }`}
                  onClick={() => toggleOption(option)}
                >
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                    {isSelected && (
                      <Check
                        className="h-4 w-4 text-slate-900"
                        strokeWidth={2}
                      />
                    )}
                  </span>
                  <span className="min-w-0 flex-1 break-words">
                    {option}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default function CandidatesTab({
  candidates,
  pagination,
  loading,
  error,
  onScheduleVoiceCalls,
  onPageChange,
  onFiltersChange,
  isHRAdmin,
}: CandidatesTabProps) {
  const navigate = useNavigate();

  const {
    widths: tableWidths,
    childWidths,
    tableWidth,
    childTableWidth,
  } = useMemo(
    () => getTableWidths(isHRAdmin),
    [isHRAdmin]
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedAllocationStatuses, setSelectedAllocationStatuses] =
    useState<AllocationFilterValue[]>([]);
  const [minATSScore, setMinATSScore] = useState("");
  const [expandedCandidates, setExpandedCandidates] = useState<string[]>([]);
  const hasMountedFilters = useRef(false);
  const onFiltersChangeRef = useRef(onFiltersChange);

  useEffect(() => {
    onFiltersChangeRef.current = onFiltersChange;
  }, [onFiltersChange]);

  // Send global filters to the backend. Skip the first render because
  // useHRDashboard already performs the initial unfiltered fetch.
  useEffect(() => {
    if (!hasMountedFilters.current) {
      hasMountedFilters.current = true;
      return;
    }

    const timer = window.setTimeout(() => {
      onFiltersChangeRef.current({
        search: searchQuery.trim(),
        department: selectedDepartments[0] ?? "All",
        status: selectedStatuses[0] ?? "All",
        minATSScore,
      });
    }, 300);

    return () => window.clearTimeout(timer);
  }, [
    searchQuery,
    selectedDepartments,
    selectedStatuses,
    minATSScore,
  ]);

  const departments = [
    ...new Set(DEPARTMENTS.map(normalizeDepartment).filter(Boolean)),
  ];

  const allocationOptions: AllocationFilterValue[] = [
    "Available",
    "Allocated",
    "Blacklisted",
  ];

  const toggleApplications = (candidateId: string) => {
    setExpandedCandidates((previous) =>
      previous.includes(candidateId)
        ? previous.filter((id) => id !== candidateId)
        : [...previous, candidateId]
    );
  };
  const filteredApplicationsByCandidate = useMemo(() => {
    const search = searchQuery.trim().toLowerCase();
    const result = new Map<string, CandidateApplicationSummary[]>();

    candidates.forEach((candidate) => {
      const candidateWithApplications =
        candidate as CandidateWithAllocation;

      const applications = candidateWithApplications.applications ?? [];
      const matchingApplications = applications.filter((application) => {
        const applicationDepartment = normalizeDepartment(
          application.department
        );

        const applicationStatus = getLatestStatusValue(
          application.status,
          application.currentStatus
        );

        const normalizedStatus = normalizeCandidateFilterStatus({
          ...candidate,
          currentStage: {
            ...candidate.currentStage,
            status: applicationStatus,
          },
          currentStatus: applicationStatus,
          status: application.status ?? [],
        });

        const applicationScore = Number(application.atsScore);

        const matchesDepartment =
          selectedDepartments.length === 0 ||
          selectedDepartments.includes(applicationDepartment);

        const matchesStatus =
          selectedStatuses.length === 0 ||
          selectedStatuses.includes(normalizedStatus);

        const matchesATSScore =
          !minATSScore ||
          (Number.isFinite(applicationScore) &&
            applicationScore >= Number(minATSScore));

        const searchableValues = [
          candidate.fullName,
          candidate.email,
          candidate.phone,
          candidate.currentDesignation,
          candidate.currentCompany,
          candidate.currentLocation,
          application.jobTitle,
          application.department,
          application.currentStatus,
          applicationStatus,
        ];

        const matchesSearch =
          !search ||
          searchableValues.some((value) =>
            String(value ?? "").toLowerCase().includes(search)
          );

        return (
          matchesDepartment &&
          matchesStatus &&
          matchesATSScore &&
          matchesSearch
        );
      });

      result.set(String(candidate.id), matchingApplications);
    });

    return result;
  }, [
    candidates,
    searchQuery,
    selectedDepartments,
    selectedStatuses,
    minATSScore,
  ]);

  // Search, department, status and ATS score are filtered globally by the
  // backend. Keep only Allocation Status as a local filter on the current page.
  const filteredCandidates = useMemo(() => {
    return candidates.filter((candidate) => {

      const allocationDetails = getAllocationDetails(candidate);


      let allocationFilterValue: AllocationFilterValue = "Available";

      if (allocationDetails.isBlacklisted) {
        allocationFilterValue = "Blacklisted";
      } else if (allocationDetails.isAllocated) {
        allocationFilterValue = "Allocated";
      }

      return (
        selectedAllocationStatuses.length === 0 ||
        selectedAllocationStatuses.includes(allocationFilterValue)
      );
    });
  }, [candidates, selectedAllocationStatuses]);

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedDepartments([]);
    setSelectedStatuses([]);
    setSelectedAllocationStatuses([]);
    setMinATSScore("");
  };

  const activeFilterCount =
    selectedDepartments.length +
    selectedStatuses.length +
    selectedAllocationStatuses.length +
    (minATSScore ? 1 : 0);

  return (
    <TabsContent value="candidates">
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_100px]">
            <div className="relative min-w-0">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search candidates..."
                className="h-10 w-full pl-10"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </div>

            <MultiSelectFilter
              label="Departments"
              options={departments}
              selected={selectedDepartments}
              onChange={setSelectedDepartments}
              singleSelect
            />

            <MultiSelectFilter
              label="Application Status"
              options={[...CANDIDATE_STATUS_OPTIONS].filter(
                (status) => status !== "All"
              )}
              selected={selectedStatuses}
              onChange={setSelectedStatuses}
              singleSelect
            />

            <MultiSelectFilter
              label="Allocation Status"
              options={allocationOptions}
              selected={selectedAllocationStatuses}
              onChange={(values) =>
                setSelectedAllocationStatuses(
                  values as AllocationFilterValue[]
                )
              }
              singleSelect
            />

            <Input
              type="number"
              min="0"
              max="100"
              placeholder="Min ATS"
              className="h-10 w-full"
              value={minATSScore}
              onChange={(event) => setMinATSScore(event.target.value)}
            />
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4 text-sm text-slate-500">
            <div>
              Showing{" "}
              <span className="font-semibold text-slate-900">
                {filteredCandidates.length}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-slate-900">
                {pagination?.totalRecords ?? 0}
              </span>{" "}
              candidates
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {selectedDepartments.map((department) => (
                <Badge
                  key={department}
                  variant="outline"
                  className="gap-1"
                >
                  {department}
                  <button
                    type="button"
                    aria-label={`Remove ${department} filter`}
                    onClick={() =>
                      setSelectedDepartments((values) =>
                        values.filter((value) => value !== department)
                      )
                    }
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}

              {selectedStatuses.map((status) => (
                <Badge key={status} variant="outline" className="gap-1">
                  {status}
                  <button
                    type="button"
                    aria-label={`Remove ${status} filter`}
                    onClick={() =>
                      setSelectedStatuses((values) =>
                        values.filter((value) => value !== status)
                      )
                    }
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}

              {selectedAllocationStatuses.map((status) => (
                <Badge key={status} variant="outline" className="gap-1">
                  Allocation: {status}
                  <button
                    type="button"
                    aria-label={`Remove ${status} allocation filter`}
                    onClick={() =>
                      setSelectedAllocationStatuses((values) =>
                        values.filter((value) => value !== status)
                      )
                    }
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}

              {minATSScore && (
                <Badge variant="outline">ATS ≥ {minATSScore}</Badge>
              )}

              {activeFilterCount > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="h-7"
                >
                  Clear filters
                </Button>
              )}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={onScheduleVoiceCalls}
            >
              <PhoneCall className="mr-2 h-4 w-4" />
              Schedule Voice Calls
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Card className="mb-6 border-red-200">
          <CardContent className="py-4 text-sm text-red-600">
            {error}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="max-h-[600px] w-full overflow-y-auto overflow-x-auto">
            <Table
              className="table-fixed border-separate border-spacing-0 text-sm"
              style={{ width: tableWidth, minWidth: tableWidth }}
            >
              <ColGroup widths={tableWidths} />
              <TableHeader className="sticky top-0 z-10">
                <TableRow className="border-b border-slate-200 bg-white hover:bg-white">
                  <TableHead className="h-9 whitespace-nowrap border-b border-slate-200 px-4 text-sm font-medium normal-case tracking-normal text-slate-600">
                    Candidate
                  </TableHead>
                  <TableHead className="h-9 whitespace-nowrap border-b border-slate-200 px-4 text-sm font-medium normal-case tracking-normal text-slate-600">
                    Position
                  </TableHead>

                  {isHRAdmin && (
                    <TableHead className="h-9 whitespace-nowrap border-b border-slate-200 px-4 text-sm font-medium normal-case tracking-normal text-slate-600">
                      Referred By
                    </TableHead>
                  )}
                  <TableHead className="h-9 whitespace-nowrap border-b border-slate-200 px-4 text-sm font-medium normal-case tracking-normal text-slate-600">
                    Department
                  </TableHead>
                  <TableHead className="h-9 whitespace-nowrap border-b border-slate-200 px-4 text-sm font-medium normal-case tracking-normal text-slate-600">
                    ATS Score
                  </TableHead>
                  <TableHead className="h-9 whitespace-nowrap border-b border-slate-200 px-4 text-sm font-medium normal-case tracking-normal text-slate-600">
                    Status
                  </TableHead>
                  <TableHead className="h-9 whitespace-nowrap border-b border-slate-200 px-4 text-sm font-medium normal-case tracking-normal text-slate-600">
                    Allocation Status
                  </TableHead>
                  <TableHead className="h-9 whitespace-nowrap border-b border-slate-200 px-4 text-sm font-medium normal-case tracking-normal text-slate-600">
                    Applications
                  </TableHead>
                  <TableHead className="h-9 whitespace-nowrap border-b border-slate-200 px-4 text-sm font-medium normal-case tracking-normal text-slate-600">
                    Voice Call
                  </TableHead>
                  <TableHead className="h-9 whitespace-nowrap border-b border-slate-200 px-4 text-sm font-medium normal-case tracking-normal text-slate-600">
                    Applied
                  </TableHead>
                  <TableHead className="h-9 whitespace-nowrap border-b border-slate-200 px-5 text-right text-sm font-medium normal-case tracking-normal text-slate-600">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={isHRAdmin ? 11 : 10} className="py-12 text-center">
                      Loading candidates...
                    </TableCell>
                  </TableRow>
                ) : filteredCandidates.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={isHRAdmin ? 11 : 10}
                      className="py-12 text-center text-muted-foreground"
                    >
                      No candidates found for the selected filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCandidates.map((candidate) => {
                    const candidateWithApplications =
                      candidate as CandidateWithAllocation;

                    const candidateRecord = candidate as CandidateListItem & {
                      applicationId?: string | null;
                      jobId?: string | null;
                    };


                    const uniqueCandidateApplications = Array.from(
                      new Map(
                        (candidateWithApplications.applications ?? []).map(
                          (application, applicationIndex) => [
                            application.applicationId ||
                            application.id ||
                            `${candidate.id}-application-${applicationIndex}`,
                            application,
                          ]
                        )
                      ).values()
                    );


                    const locallyMatchingApplications =
                      filteredApplicationsByCandidate.get(String(candidate.id)) ?? [];

                    // Backend is the source of truth for global filtering.
                    // If local fields differ from backend fields, keep the candidate
                    // visible and fall back to its applications for row rendering.
                    const matchingApplications =
                      locallyMatchingApplications.length > 0
                        ? locallyMatchingApplications
                        : uniqueCandidateApplications;

                    // Main row mein pehli matching (ya fallback) application dikhegi.
                    const primaryApplication = matchingApplications[0];

                    // Main row wali application dropdown mein repeat nahi hogi.
                    const visibleCandidateApplications = matchingApplications.filter(
                      (application) => {
                        if (!primaryApplication) return false;

                        // Main row aur dropdown mein same application object ho toh remove karein.
                        if (application === primaryApplication) return false;

                        const primaryId = String(
                          primaryApplication.applicationId || primaryApplication.id || ""
                        );

                        const currentId = String(
                          application.applicationId || application.id || ""
                        );

                        // Same application ID ko dropdown mein dobara mat dikhayein.
                        if (primaryId && currentId && primaryId === currentId) {
                          return false;
                        }

                        return true;
                      }
                    );

                    const isApplicationsExpanded =
                      expandedCandidates.includes(String(candidate.id));


                    const applicationStatus = primaryApplication
                      ? getLatestStatusValue(
                        primaryApplication.status,
                        primaryApplication.currentStatus
                      )
                      : getLatestStatusValue(
                        candidate.status,
                        candidate.currentStage?.status ?? candidate.currentStatus
                      );

                    const status = normalizeCandidateFilterStatus({
                      ...candidate,
                      currentStage: {
                        ...candidate.currentStage,
                        status: applicationStatus,
                      },
                      currentStatus: applicationStatus,
                      status: primaryApplication?.status ?? candidate.status ?? [],
                    });

                    const displayDepartment = primaryApplication
                      ? normalizeDepartment(primaryApplication.department) || "—"
                      : candidate.currentStage?.department ||
                      normalizeDepartment(candidate.department) ||
                      "—";


                    const primaryApplicationId =
                      primaryApplication?.applicationId ||
                      primaryApplication?.id ||
                      candidate.id;

                    const allocationDetails = getAllocationDetails(
                      candidate,
                      primaryApplicationId
                    );

                    const allocationLabel =
                      getAllocationLabel(allocationDetails);

                    const initials = getInitials(
                      candidate.fullName ?? "",
                      "Candidate"
                    );

                    const canManageProcess =
                      allocationDetails.canManageProcess;

                    return (
                      <>
                        <TableRow
                          key={candidate.id}
                          className={`group cursor-pointer border-b border-slate-200 transition-colors duration-150 ${isApplicationsExpanded ? "bg-slate-50 hover:bg-slate-50" : "bg-white hover:bg-slate-50"}`}
                          onClick={() =>
                            navigate(`/hr/candidates/${candidate.id}`)
                          }
                        >
                          <TableCell className="border-b border-slate-100 px-4 py-4 align-middle">
                            <div className="flex items-center gap-2">
                              {matchingApplications.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 shrink-0"
                                  aria-label={
                                    isApplicationsExpanded
                                      ? "Collapse applications"
                                      : "Expand applications"
                                  }
                                  aria-expanded={isApplicationsExpanded}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    toggleApplications(String(candidate.id));
                                  }}
                                >
                                  <ChevronDown
                                    className={`h-4 w-4 transition-transform ${isApplicationsExpanded ? "rotate-180" : ""
                                      }`}
                                  />
                                </Button>
                              )}

                              {matchingApplications.length <= 1 && (
                                <div className="h-7 w-7 shrink-0" />
                              )}

                              {/* Candidate Circle */}
                              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50 text-sm font-semibold text-blue-700 ring-1 ring-inset ring-blue-100">
                                {initials}
                              </div>

                              {/* Candidate Details */}
                              <div className="min-w-0">
                                <p className="whitespace-nowrap text-sm font-medium text-slate-900">
                                  {candidate.fullName || "—"}
                                </p>

                                <p className="max-w-[240px] truncate text-xs font-normal text-slate-500">
                                  {candidate.email || "—"}
                                </p>
                              </div>
                            </div>
                          </TableCell>

                          <TableCell className="border-b border-slate-100 px-4 py-2 align-middle">
                            <p className="text-sm font-medium text-slate-900">
                              {primaryApplication?.jobTitle || candidate.jobTitle || "—"}
                            </p>
                            <p className="mt-0.5 text-xs font-normal text-slate-500">
                              {candidate.currentCompany || "—"}
                            </p>
                          </TableCell>

                          {isHRAdmin && (
                            <TableCell className="border-b border-slate-100 px-4 py-2 align-middle">
                              <span className="text-sm font-medium text-slate-700">
                                {primaryApplication?.referredByName || "—"}
                              </span>
                            </TableCell>
                          )}

                          <TableCell className="border-b border-slate-100 px-4 py-2 align-middle">
                            <Badge
                              variant="outline"
                              className={
                                displayDepartment === "Human Resource"
                                  ? "border-purple-200 bg-purple-50 text-purple-700"
                                  : ""
                              }
                            >
                              {displayDepartment}
                            </Badge>
                          </TableCell>

                          <TableCell className="border-b border-slate-100 px-4 py-2 align-middle">

                            {(primaryApplication?.atsScore ?? candidate.atsScore) !== null &&
                              (primaryApplication?.atsScore ?? candidate.atsScore) !== undefined ? (
                              <div
                                className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-sm font-medium ${getScoreColorClass(
                                  Number(primaryApplication?.atsScore ?? candidate.atsScore)
                                )}`}
                              >
                                {primaryApplication?.atsScore ?? candidate.atsScore}%
                              </div>
                            ) : (
                              <Badge variant="secondary">N/A</Badge>
                            )}

                          </TableCell>

                          <TableCell className="border-b border-slate-100 px-4 py-2 align-middle">
                            <div className="flex min-w-0 flex-col gap-1">
                              <Badge
                                variant="outline"
                                className={`w-fit ${getStatusColor(status)}`}
                              >
                                {status || "N/A"}
                              </Badge>
                            </div>
                          </TableCell>

                          <TableCell className="border-b border-slate-100 px-4 py-2 align-middle">
                            <div className="flex min-w-[220px] flex-col items-start gap-1.5">
                              <Badge
                                variant="outline"
                                className={`${getAllocationBadgeClass(
                                  allocationDetails
                                )} inline-flex w-max max-w-none shrink-0 whitespace-nowrap`}
                              >
                                {allocationLabel}
                              </Badge>

                              {!allocationDetails.isBlacklisted &&
                                allocationDetails.isAllocated &&
                                !allocationDetails.ownerName && (
                                  <span className="text-xs text-muted-foreground">
                                    Owner details unavailable
                                  </span>
                                )}
                            </div>
                          </TableCell>

                          <TableCell className="border-b border-slate-100 px-4 py-2 align-middle">
                            <span className="text-sm font-semibold text-slate-700">
                              {uniqueCandidateApplications.length}
                            </span>
                          </TableCell>

                          <TableCell className="border-b border-slate-100 px-4 py-2 align-middle">
                            <Badge variant="secondary">Pending</Badge>
                          </TableCell>

                          <TableCell className="px-4 py-2 align-middle text-sm text-slate-500">
                            {candidate.createdAt
                              ? formatDate(candidate.createdAt)
                              : "—"}
                          </TableCell>

                          <TableCell className="border-b border-slate-100 px-4 py-2 text-right align-middle">
                            <DropdownMenu>
                              <DropdownMenuTrigger
                                asChild
                                onClick={(event) => event.stopPropagation()}
                              >
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>

                              <DropdownMenuContent
                                align="end"
                                onClick={(event) => event.stopPropagation()}
                              >
                                <DropdownMenuItem
                                  onSelect={() =>
                                    navigate(`/hr/candidates/${candidate.id}`)
                                  }
                                >
                                  <Eye className="mr-2 h-4 w-4" />
                                  View Details
                                </DropdownMenuItem>

                                <DropdownMenuSeparator />

                                <DropdownMenuItem disabled={!canManageProcess}>
                                  <Phone className="mr-2 h-4 w-4" />
                                  Make Voice Call
                                </DropdownMenuItem>

                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                        {isApplicationsExpanded &&
                          matchingApplications.length > 1 &&
                          visibleCandidateApplications.length > 0 && (
                            <TableRow className="bg-slate-50 hover:bg-slate-50">
                              <TableCell colSpan={isHRAdmin ? 11 : 10} className="p-0 align-top">
                                <div className="mx-5 my-3 overflow-hidden rounded-lg border border-blue-200 bg-white shadow-sm">
                                  <div
                                    className={
                                      visibleCandidateApplications.length >= 3
                                        ? "max-h-[240px] overflow-y-auto overflow-x-auto"
                                        : "overflow-x-auto"
                                    }
                                  >
                                    <Table
                                      className="table-fixed border-separate border-spacing-0 text-sm"
                                      style={{
                                        width: childTableWidth,
                                        minWidth: childTableWidth,
                                      }}
                                    >
                                      <ColGroup widths={childWidths} />
                                      <TableBody>
                                        {visibleCandidateApplications.map((application, applicationIndex) => {
                                          const applicationId =
                                            application.applicationId || application.id || "";


                                          const childCanManageProcess =
                                            getAllocationDetails(
                                              candidate,
                                              applicationId
                                            ).canManageProcess;
                                          const applicationStatus = getLatestStatusValue(
                                            application.status,
                                            application.currentStatus
                                          );

                                          const applicationDepartment =
                                            normalizeDepartment(application.department) || "—";

                                          const applicationJobTitle = application.jobTitle || "—";

                                          return (
                                            <TableRow
                                              key={
                                                applicationId ||
                                                `${candidate.id}-application-${applicationIndex}`
                                              }
                                              className="cursor-pointer border-b border-blue-100 bg-blue-50 transition-colors hover:bg-blue-100"
                                              onClick={() => {
                                                if (applicationId) {
                                                  navigate(`/hr/candidates/${applicationId}`);
                                                }
                                              }}
                                            >
                                              {/* 1. Candidate */}
                                              <TableCell className="min-w-[200px] border-b border-blue-100 px-3 py-2 align-middle">
                                                <div className="flex items-center gap-2 pl-9">
                                                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-700 ring-1 ring-inset ring-blue-200">
                                                    {initials}
                                                  </div>

                                                  <div className="min-w-0">
                                                    <p className="whitespace-nowrap text-sm font-medium text-slate-900">
                                                      {candidate.fullName || "—"}
                                                    </p>
                                                    <p className="max-w-[240px] truncate text-xs text-slate-500">
                                                      {candidate.email || "—"}
                                                    </p>
                                                  </div>
                                                </div>
                                              </TableCell>

                                              {/* 2. Position */}
                                              <TableCell className="min-w-[160px] border-b border-blue-100 px-3 py-2 align-middle">
                                                <p className="text-sm font-medium text-slate-900">
                                                  {applicationJobTitle}
                                                </p>
                                                <p className="mt-0.5 text-xs text-slate-500">
                                                  {candidate.currentCompany || "—"}
                                                </p>
                                              </TableCell>

                                              {isHRAdmin && (
                                                <TableCell className="border-b border-blue-100 px-3 py-2 align-middle">
                                                  <span className="text-sm font-medium text-slate-700">
                                                    {application.referredByName || "—"}
                                                  </span>
                                                </TableCell>
                                              )}

                                              {/* 3. Department */}
                                              <TableCell className="min-w-[120px] border-b border-blue-100 px-3 py-2 align-middle">
                                                <Badge
                                                  variant="outline"
                                                  className={
                                                    applicationDepartment === "Human Resource"
                                                      ? "border-purple-200 bg-purple-50 text-purple-700"
                                                      : ""
                                                  }
                                                >
                                                  {applicationDepartment}
                                                </Badge>
                                              </TableCell>

                                              {/* 4. ATS Score */}
                                              <TableCell className="min-w-[90px] border-b border-blue-100 px-3 py-2 align-middle">
                                                {application.atsScore !== null &&
                                                  application.atsScore !== undefined ? (
                                                  <div
                                                    className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-sm font-medium ${getScoreColorClass(
                                                      Number(application.atsScore)
                                                    )}`}
                                                  >
                                                    {application.atsScore}%
                                                  </div>
                                                ) : (
                                                  <Badge variant="secondary">N/A</Badge>
                                                )}
                                              </TableCell>

                                              {/* 5. Status */}
                                              <TableCell className="min-w-[160px] border-b border-blue-100 px-3 py-2 align-middle">
                                                <div className="flex min-w-0 flex-col gap-1">
                                                  <Badge
                                                    variant="outline"
                                                    className={`w-fit ${getStatusColor(applicationStatus)}`}
                                                  >
                                                    {applicationStatus.replace(/_/g, " ")}
                                                  </Badge>

                                                </div>
                                              </TableCell>

                                              {/* 6. Allocation Status */}
                                              <TableCell className="min-w-[200px] border-b border-blue-100 px-3 py-2 align-middle">
                                                <div className="flex min-w-[220px] flex-col items-start gap-1.5">
                                                  <div className="flex w-full min-w-0 flex-col items-start gap-1.5">
                                                    <Badge
                                                      variant="outline"
                                                      className={`${getAllocationBadgeClass(
                                                        allocationDetails
                                                      )} inline-flex w-max max-w-none shrink-0 whitespace-nowrap`}
                                                    >
                                                      {allocationLabel}
                                                    </Badge>
                                                  </div>

                                                  {!allocationDetails.isBlacklisted &&
                                                    allocationDetails.isAllocated &&
                                                    !allocationDetails.ownerName && (
                                                      <span className="text-xs text-muted-foreground">
                                                        Owner details unavailable
                                                      </span>
                                                    )}
                                                </div>
                                              </TableCell>

                                              {/* 7. Applications count is shown on the candidate's main row only */}
                                              <TableCell className="w-[48px] min-w-[48px] max-w-[48px] border-b border-blue-100 px-1 py-2 align-middle" />

                                              {/* 8. Voice Call */}
                                              <TableCell className="min-w-[90px] border-b border-blue-100 px-3 py-2 align-middle">
                                                <Badge variant="secondary">Pending</Badge>
                                              </TableCell>

                                              {/* 9. Applied */}
                                              <TableCell className="min-w-[100px] border-b border-blue-100 px-3 py-2 align-middle text-sm text-slate-500">
                                                {application.createdAt
                                                  ? formatDate(application.createdAt)
                                                  : "—"}
                                              </TableCell>

                                              {/* 10. Actions */}
                                              <TableCell
                                                className="min-w-[70px] border-b border-blue-100 px-3 py-2 text-right align-middle"
                                                onClick={(event) => event.stopPropagation()}
                                              >
                                                <DropdownMenu>
                                                  <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="icon">
                                                      <MoreHorizontal className="h-4 w-4" />
                                                    </Button>
                                                  </DropdownMenuTrigger>

                                                  <DropdownMenuContent align="end">
                                                    <DropdownMenuItem
                                                      disabled={!applicationId}
                                                      onSelect={() => {
                                                        if (applicationId) {
                                                          navigate(
                                                            `/hr/candidates/${applicationId}`
                                                          );
                                                        }
                                                      }}
                                                    >
                                                      <Eye className="mr-2 h-4 w-4" />
                                                      View Details
                                                    </DropdownMenuItem>

                                                    <DropdownMenuSeparator />

                                                    <DropdownMenuItem disabled={!childCanManageProcess}>
                                                      <Phone className="mr-2 h-4 w-4" />
                                                      Make Voice Call
                                                    </DropdownMenuItem>

                                                  </DropdownMenuContent>
                                                </DropdownMenu>
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                      </>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
          <p className="text-sm text-slate-500">
            Page{" "}
            <span className="font-semibold text-slate-900">
              {pagination.currentPage}
            </span>{" "}
            of{" "}
            <span className="font-semibold text-slate-900">
              {pagination.totalPages}
            </span>
          </p>

          <div className="flex flex-wrap items-center gap-1.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={
                loading ||
                !pagination.hasPreviousPage
              }
              onClick={() =>
                onPageChange(pagination.currentPage - 1)
              }
            >
              Previous
            </Button>

            {Array.from(
              { length: Math.min(6, pagination.totalPages) },
              (_, index) => index + 1
            ).map((page) => (
              <Button
                key={page}
                type="button"
                size="sm"
                variant={
                  pagination.currentPage === page
                    ? "default"
                    : "outline"
                }
                disabled={loading}
                aria-current={
                  pagination.currentPage === page
                    ? "page"
                    : undefined
                }
                onClick={() => onPageChange(page)}
                className="min-w-9"
              >
                {page}
              </Button>
            ))}

            {pagination.totalPages > 6 && (
              <>
                <span className="px-1 text-slate-400">
                  ...
                </span>

                <Button
                  type="button"
                  size="sm"
                  variant={
                    pagination.currentPage === pagination.totalPages
                      ? "default"
                      : "outline"
                  }
                  disabled={loading}
                  onClick={() =>
                    onPageChange(pagination.totalPages)
                  }
                  className="min-w-9"
                >
                  {pagination.totalPages}
                </Button>
              </>
            )}

            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={
                loading ||
                !pagination.hasNextPage
              }
              onClick={() =>
                onPageChange(pagination.currentPage + 1)
              }
            >
              Next
            </Button>
          </div>
        </div>
      )}

    </TabsContent>
  );
}